export type ElementType = 'wood' | 'fire' | 'earth' | 'metal' | 'water'
export type YinYang = 'yang' | 'yin'

export interface Stem {
  name: string
  element: ElementType
  yinYang: YinYang
  meaning: string
}

export interface Branch {
  name: string
  animal: string
  element: ElementType
  yinYang: YinYang
  hiddenStems: string[]
  season: string
  time: string
}

export const ELEMENTS: Record<
  ElementType,
  { name: string; color: string; generates: ElementType; controls: ElementType }
> = {
  wood: {
    name: '木',
    color: 'text-element-wood',
    generates: 'fire',
    controls: 'earth',
  },
  fire: {
    name: '火',
    color: 'text-element-fire',
    generates: 'earth',
    controls: 'metal',
  },
  earth: {
    name: '土',
    color: 'text-element-earth',
    generates: 'metal',
    controls: 'water',
  },
  metal: {
    name: '金',
    color: 'text-element-metal',
    generates: 'water',
    controls: 'wood',
  },
  water: {
    name: '水',
    color: 'text-element-water',
    generates: 'wood',
    controls: 'fire',
  },
}

export const TIAN_GAN: Stem[] = [
  {
    name: '甲',
    element: 'wood',
    yinYang: 'yang',
    meaning: '参天大树，栋梁之才',
  },
  {
    name: '乙',
    element: 'wood',
    yinYang: 'yin',
    meaning: '花草藤蔓，柔韧适应',
  },
  {
    name: '丙',
    element: 'fire',
    yinYang: 'yang',
    meaning: '太阳之火，热情光芒',
  },
  {
    name: '丁',
    element: 'fire',
    yinYang: 'yin',
    meaning: '灯烛之火，温和细腻',
  },
  {
    name: '戊',
    element: 'earth',
    yinYang: 'yang',
    meaning: '高山之土，稳重包容',
  },
  {
    name: '己',
    element: 'earth',
    yinYang: 'yin',
    meaning: '田园之土，孕育万物',
  },
  {
    name: '庚',
    element: 'metal',
    yinYang: 'yang',
    meaning: '刀剑之金，刚毅果断',
  },
  {
    name: '辛',
    element: 'metal',
    yinYang: 'yin',
    meaning: '珠玉之金，精致华丽',
  },
  {
    name: '壬',
    element: 'water',
    yinYang: 'yang',
    meaning: '江河之水，奔腾不息',
  },
  {
    name: '癸',
    element: 'water',
    yinYang: 'yin',
    meaning: '雨露之水，滋润无声',
  },
]

export const DI_ZHI: Branch[] = [
  {
    name: '子',
    animal: '鼠',
    element: 'water',
    yinYang: 'yang',
    hiddenStems: ['癸'],
    season: '冬',
    time: '23:00-01:00',
  },
  {
    name: '丑',
    animal: '牛',
    element: 'earth',
    yinYang: 'yin',
    hiddenStems: ['己', '癸', '辛'],
    season: '冬',
    time: '01:00-03:00',
  },
  {
    name: '寅',
    animal: '虎',
    element: 'wood',
    yinYang: 'yang',
    hiddenStems: ['甲', '丙', '戊'],
    season: '春',
    time: '03:00-05:00',
  },
  {
    name: '卯',
    animal: '兔',
    element: 'wood',
    yinYang: 'yin',
    hiddenStems: ['乙'],
    season: '春',
    time: '05:00-07:00',
  },
  {
    name: '辰',
    animal: '龙',
    element: 'earth',
    yinYang: 'yang',
    hiddenStems: ['戊', '乙', '癸'],
    season: '春',
    time: '07:00-09:00',
  },
  {
    name: '巳',
    animal: '蛇',
    element: 'fire',
    yinYang: 'yin',
    hiddenStems: ['丙', '戊', '庚'],
    season: '夏',
    time: '09:00-11:00',
  },
  {
    name: '午',
    animal: '马',
    element: 'fire',
    yinYang: 'yang',
    hiddenStems: ['丁', '己'],
    season: '夏',
    time: '11:00-13:00',
  },
  {
    name: '未',
    animal: '羊',
    element: 'earth',
    yinYang: 'yin',
    hiddenStems: ['己', '丁', '乙'],
    season: '夏',
    time: '13:00-15:00',
  },
  {
    name: '申',
    animal: '猴',
    element: 'metal',
    yinYang: 'yang',
    hiddenStems: ['庚', '壬', '戊'],
    season: '秋',
    time: '15:00-17:00',
  },
  {
    name: '酉',
    animal: '鸡',
    element: 'metal',
    yinYang: 'yin',
    hiddenStems: ['辛'],
    season: '秋',
    time: '17:00-19:00',
  },
  {
    name: '戌',
    animal: '狗',
    element: 'earth',
    yinYang: 'yang',
    hiddenStems: ['戊', '辛', '丁'],
    season: '秋',
    time: '19:00-21:00',
  },
  {
    name: '亥',
    animal: '猪',
    element: 'water',
    yinYang: 'yin',
    hiddenStems: ['壬', '甲'],
    season: '冬',
    time: '21:00-23:00',
  },
]

export const SHI_SHEN_MAP: Record<string, string> = {
  same_same: '比肩',
  same_diff: '劫财',
  gen_same: '食神',
  gen_diff: '伤官',
  ctrl_same: '偏财',
  ctrl_diff: '正财',
  ctrled_same: '七杀',
  ctrled_diff: '正官',
  gened_same: '偏印',
  gened_diff: '正印',
}

export const NA_YIN: Record<string, string> = {
  甲子: '海中金',
  乙丑: '海中金',
  丙寅: '炉中火',
  丁卯: '炉中火',
  戊辰: '大林木',
  己巳: '大林木',
  庚午: '路旁土',
  辛未: '路旁土',
  壬申: '剑锋金',
  癸酉: '剑锋金',
  甲戌: '山头火',
  乙亥: '山头火',
  丙子: '涧下水',
  丁丑: '涧下水',
  戊寅: '城头土',
  己卯: '城头土',
  庚辰: '白蜡金',
  辛巳: '白蜡金',
  壬午: '杨柳木',
  癸未: '杨柳木',
  甲申: '泉中水',
  乙酉: '泉中水',
  丙戌: '屋上土',
  丁亥: '屋上土',
  戊子: '霹雳火',
  己丑: '霹雳火',
  庚寅: '松柏木',
  辛卯: '松柏木',
  壬辰: '长流水',
  癸巳: '长流水',
  甲午: '砂石金',
  乙未: '砂石金',
  丙申: '山下火',
  丁酉: '山下火',
  戊戌: '平地木',
  己亥: '平地木',
  庚子: '壁上土',
  辛丑: '壁上土',
  壬寅: '金箔金',
  癸卯: '金箔金',
  甲辰: '覆灯火',
  乙巳: '覆灯火',
  丙午: '天河水',
  丁未: '天河水',
  戊申: '大驿土',
  己酉: '大驿土',
  庚戌: '钗钏金',
  辛亥: '钗钏金',
  壬子: '桑柘木',
  癸丑: '桑柘木',
  甲寅: '大溪水',
  乙卯: '大溪水',
  丙辰: '沙中土',
  丁巳: '沙中土',
  戊午: '天上火',
  己未: '天上火',
  庚申: '石榴木',
  辛酉: '石榴木',
  壬戌: '大海水',
  癸亥: '大海水',
}

export const SHEN_SHA_RULES = {
  tianYi: {
    甲: ['丑', '未'],
    戊: ['丑', '未'],
    庚: ['丑', '未'],
    乙: ['子', '申'],
    己: ['子', '申'],
    丙: ['亥', '酉'],
    丁: ['亥', '酉'],
    壬: ['卯', '巳'],
    癸: ['卯', '巳'],
    辛: ['寅', '午'],
  },
  yiMa: {
    申: '寅',
    子: '寅',
    辰: '寅',
    寅: '申',
    午: '申',
    戌: '申',
    亥: '巳',
    卯: '巳',
    未: '巳',
    巳: '亥',
    酉: '亥',
    丑: '亥',
  },
  taoHua: {
    申: '酉',
    子: '酉',
    辰: '酉',
    寅: '卯',
    午: '卯',
    戌: '卯',
    亥: '子',
    卯: '子',
    未: '子',
    巳: '午',
    酉: '午',
    丑: '午',
  },
  huaGai: {
    寅: '戌',
    午: '戌',
    戌: '戌',
    申: '辰',
    子: '辰',
    辰: '辰',
    亥: '未',
    卯: '未',
    未: '未',
    巳: '丑',
    酉: '丑',
    丑: '丑',
  },
  yangRen: { 甲: '卯', 丙: '午', 戊: '午', 庚: '酉', 壬: '子' },
  luShen: {
    甲: '寅',
    乙: '卯',
    丙: '巳',
    丁: '午',
    戊: '巳',
    己: '午',
    庚: '申',
    辛: '酉',
    壬: '亥',
    癸: '子',
  },
}
